import { createContext, useEffect, useEffectEvent, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { setSentryAgentInfo } from 'services/sentry';
import { AGENT_ROLES } from 'utilities/constants';

export const AgentContext = createContext(null);

const AgentProvider = ({ children }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const ldClient = useLDClient();

  const [oktaUser, setOktaUser] = useState();
  const [agent, setAgent] = useState({
    hasAccess: false,
    isCommandManager: false,
    isSuperUser: false,
    loaded: false,
    roles: [],
    user: {},
  });

  const getOktaUserInfo = useEffectEvent(async () => {
    try {
      const user = await oktaAuth.getUser();
      setOktaUser(user);
    } catch (error) {
      console.error('There was a problem getting okta user details', error);
    }
  });

  const setAgentInfo = useEffectEvent(() => {
    const hasAccess = !!authState.idToken?.claims?.groups?.find(
      (group) => group.toLowerCase() === 'ci tool access',
    );

    const isSuperUser = !!authState.idToken?.claims?.groups?.find(
      (group) => group.toLowerCase() === 'ci tool super users',
    );

    const isCommandManager =
      authState.idToken?.claims?.roles.toLowerCase() === 'command-manager';

    const roles = [];
    hasAccess && roles.push(AGENT_ROLES.AGENT);
    isCommandManager && roles.push(AGENT_ROLES.COMMAND_MANAGER);
    isSuperUser && roles.push(AGENT_ROLES.SUPER_USER);

    setSentryAgentInfo(oktaUser);
    setAgent({
      hasAccess,
      isCommandManager,
      isSuperUser,
      loaded: true,
      roles,
      user: oktaUser,
    });
  });

  useEffect(() => {
    if (authState?.isAuthenticated) {
      getOktaUserInfo();
    }
  }, [authState]);

  useEffect(() => {
    if (oktaUser) {
      setAgentInfo();
    }
  }, [oktaUser]);

  useEffect(
    function identifyAgentWithLaunchDarkly() {
      if (ldClient && oktaUser) {
        const userEmail = oktaUser.requester_id || '';
        ldClient.identify({
          key: userEmail.toLowerCase(),
          anonymous: false,
        });
      }
    },
    [ldClient, oktaUser],
  );

  useEffect(
    function identifyAgentWithFullStory() {
      if (oktaUser) {
        const userEmail = oktaUser.requester_id || '';
        window.FS?.identify(userEmail, {
          displayName: oktaUser.name || '',
          email: userEmail,
        });
      }
    },
    [oktaUser],
  );

  return (
    <AgentContext.Provider value={agent}>{children}</AgentContext.Provider>
  );
};

export default AgentProvider;
